import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Pencil, Send, Sparkles, X } from "lucide-react";
import type { TimelineChat } from "./_shared";
import { useAutoDraftComposer } from "./auto-draft-composer-context";

/** "Auto-draft · sends at 3:42 PM" / "Auto-draft · waiting for you". */
export function autoDraftStatusLabel(pending: { autoSend: boolean; sendAt: string | null }, now: Date = new Date()): string {
  if (!pending.autoSend || !pending.sendAt) return "Auto-draft · waiting for you";
  const at = new Date(pending.sendAt);
  if (isNaN(at.getTime())) return "Auto-draft · waiting for you";
  const time = at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const sameDay = at.toDateString() === now.toDateString();
  const when = sameDay ? time : `${at.toLocaleDateString([], { month: "short", day: "numeric" })}, ${time}`;
  return at.getTime() <= now.getTime() ? "Auto-draft · sending now" : `Auto-draft · sends at ${when}`;
}

/**
 * Messages Auto Draft — an AI-written reply waiting in the member's chat.
 * Visibly distinct from a host-queued message, with Send now / Edit / Cancel.
 */
export function AutoDraftBubble({ chat, memberId }: { chat: TimelineChat; memberId: number }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const composer = useAutoDraftComposer();
  const pending = chat.pending!;
  const text = chat.messageText ?? "";
  const [editing, setEditing] = useState(false);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["/api/app/members", memberId, "timeline"] });
    queryClient.invalidateQueries({ queryKey: ["/api/app/messages/pending"] });
    queryClient.invalidateQueries({ queryKey: ["/api/app/inbox"] });
  }

  const sendNowMutation = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/app/messages/pending/${pending.id}/confirm`, { sendNow: true }),
    onSuccess: () => {
      toast({ title: "Sending now" });
      invalidate();
    },
    onError: (err) => toast({ title: "Couldn't send", description: parseErrorMessage(err, "Couldn't send. Please try again."), variant: "destructive" }),
  });

  const cancelMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => apiRequest("POST", `/api/app/messages/pending/${pending.id}/cancel`),
    onSuccess: () => {
      toast({ title: "Auto-draft cancelled" });
      invalidate();
    },
    onError: (err) => toast({ title: "Couldn't cancel", description: parseErrorMessage(err, "Couldn't cancel. Please try again."), variant: "destructive" }),
  });

  // Edit = stop the timer (cancel the queued row) and hand the text to the
  // reply box for the host to send manually. Ask first if they're typing.
  async function handleEdit() {
    if (!composer || editing) return;
    const ok = await composer.confirmReplace();
    if (!ok) return;
    setEditing(true);
    try {
      await apiRequest("POST", `/api/app/messages/pending/${pending.id}/cancel`);
      composer.load(text);
      invalidate();
      toast({ title: "Draft moved to the reply box", description: "It won't send on its own. Edit it and send when ready." });
    } catch (err) {
      toast({ title: "Couldn't edit", description: parseErrorMessage(err, "Couldn't move the draft. Please try again."), variant: "destructive" });
    } finally {
      setEditing(false);
    }
  }

  const busy = sendNowMutation.isPending || cancelMutation.isPending || editing;
  const label = autoDraftStatusLabel(pending);

  return (
    <div className="group mb-1 flex flex-col items-end" data-testid={`bubble-auto-draft-${pending.id}`}>
      <div className="max-w-[80%] rounded-lg border border-dashed border-violet-400 bg-violet-50 px-3 py-2 text-sm text-gray-900 dark:border-violet-500/70 dark:bg-violet-950/40 dark:text-gray-100">
        <div className="mb-1 flex items-center gap-1.5 text-[11px] font-medium text-violet-700 dark:text-violet-300">
          <Sparkles className="h-3 w-3" aria-hidden />
          <span data-testid={`text-auto-draft-status-${pending.id}`}>{label}</span>
          <span className="rounded bg-violet-200/70 px-1 py-px text-[9px] font-semibold uppercase leading-none tracking-wide text-violet-800 dark:bg-violet-800/60 dark:text-violet-100">
            AI
          </span>
        </div>
        <div className="whitespace-pre-wrap break-words" data-testid={`text-auto-draft-body-${pending.id}`}>
          {text}
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-end gap-1.5">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs text-gray-600 hover:text-red-600 dark:text-gray-300"
            onClick={() => cancelMutation.mutate()}
            disabled={busy}
            data-testid={`button-auto-draft-cancel-${pending.id}`}
          >
            {cancelMutation.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <X className="mr-1 h-3 w-3" />}
            Cancel
          </Button>
          {composer && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs"
              onClick={() => void handleEdit()}
              disabled={busy}
              data-testid={`button-auto-draft-edit-${pending.id}`}
            >
              {editing ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Pencil className="mr-1 h-3 w-3" />}
              Edit
            </Button>
          )}
          <Button
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => sendNowMutation.mutate()}
            disabled={busy}
            data-testid={`button-auto-draft-send-now-${pending.id}`}
          >
            {sendNowMutation.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Send className="mr-1 h-3 w-3" />}
            Send now
          </Button>
        </div>
      </div>
    </div>
  );
}
